import * as React from 'react'
import { useSelector } from 'react-redux'
import {
  Autocomplete,
  Box,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography as MIUTypography,
  styled,
} from '@mui/material'
import { DataGrid, gridClasses, esES } from '@mui/x-data-grid'
import { getFamilyTableAll, getSpecieTableAll, updateSpecieTableById } from '../../../services'
import { grey } from '@mui/material/colors'
import { getColumns } from './columns'
import Botton from '../../BottonCustom'
import { Create } from '@mui/icons-material'
import CreationModal from './CreationModal'
import DeleteSpecieModal from './DeleteSpecieModal'
import { usePersistedState } from '../../../hooks/usePersistedState'
import AlertCustom from '../../Alerts'

const INACTIVE_GROUP_KEY = 'inactive'
const INACTIVE_GROUP_LABEL = 'Sin familia activa'

const styleContainer = { height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }

const styleDefault = theme => ({
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  width: '100%',
  fontSize: '12px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  '&:active': { boxShadow: 'none', opacity: 0.9 },
})

const styleHeaderTab = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignContent: 'center',
  alignItems: 'baseline',
  ml: 4,
  mb: 1,
}

const Typography = styled(MIUTypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1em',
  fontWeight: 650,
  lineHeight: '18px',
  color: '#6A6A6A',
  paddingBottom: 6,
  paddingTop: 6,
  textAlign: 'center',
  marginTop: 12,
  marginBottom: 3,
  height: '5%',
}))

const normalizeFamilies = families =>
  (families || [])
    .filter(row => row?.id !== undefined && row?.id !== null)
    .map(row => ({
      id: row.id,
      nombre: row.nombre || `Familia #${row.id}`,
      habilitado: Boolean(row.habilitado),
    }))

const buildFamilyMap = families =>
  families.reduce((acc, family) => {
    acc[`${family.id}`] = {
      nombre: family.nombre,
      habilitado: family.habilitado,
    }
    return acc
  }, {})

const enrichSpeciesRows = (speciesRows, familyMap) =>
  (speciesRows || []).map(row => {
    const familyId = row?.idFamilia
    const familyData = familyMap?.[`${familyId}`]
    const familiaActiva = Boolean(familyData?.habilitado)
    const familiaNombre = familyData?.nombre || INACTIVE_GROUP_LABEL

    return {
      ...row,
      familiaNombre,
      familiaActiva,
      familyGroupKey: familiaActiva ? `${familyId}` : INACTIVE_GROUP_KEY,
    }
  })

const buildFamilyFilterOptions = families => {
  const activeFamilies = families
    .filter(family => family.habilitado)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'accent' }))
    .map(family => ({
      key: `${family.id}`,
      label: family.nombre,
    }))

  return [...activeFamilies, { key: INACTIVE_GROUP_KEY, label: INACTIVE_GROUP_LABEL }]
}

const filterRows = (rows, scientificSearch, classifierSearch, selectedFamilyKeys) => {
  const scientificQuery = scientificSearch.trim().toLowerCase()
  const classifierQuery = classifierSearch.trim().toLowerCase()
  const selectedKeysSet = new Set(selectedFamilyKeys)

  return rows.filter(row => {
    const scientific = (row?.nombre_cientifico || '').toLowerCase()
    const classifier = (row?.clasificador || '').toLowerCase()

    const scientificMatches = !scientificQuery || scientific.includes(scientificQuery)
    const classifierMatches = !classifierQuery || classifier.includes(classifierQuery)
    const familyMatches = selectedKeysSet.size === 0 || selectedKeysSet.has(row.familyGroupKey)

    return scientificMatches && classifierMatches && familyMatches
  })
}

const SpecieTable = () => {
  const modalRef = React.useRef()
  const alertRef = React.useRef()
  const deleteModalRef = React.useRef()

  const handleRequestDelete = React.useCallback(specie => {
    deleteModalRef.current?.openModal(specie)
  }, [])

  const [data, setData] = React.useState([])
  const [families, setFamilies] = React.useState([])
  const [scientificSearch, setScientificSearch] = usePersistedState('admin.specie.scientificSearch', '')
  const [classifierSearch, setClassifierSearch] = usePersistedState('admin.specie.classifierSearch', '')
  const [selectedFamilyKeys, setSelectedFamilyKeys] = usePersistedState('admin.specie.selectedFamilyKeys', [])
  const [expandedRowIds, setExpandedRowIds] = React.useState([])
  const [familyUpdateBySpecieId, setFamilyUpdateBySpecieId] = React.useState({})
  const [pageSize, setPageSize] = usePersistedState('admin.specie.pageSize', 20)
  const [rowId, setRowId] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const { isStored } = useSelector(state => state.alertsGlobal)

  const enabledFamilyOptions = React.useMemo(
    () =>
      families
        .filter(family => family.habilitado)
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'accent' })),
    [families]
  )

  const familyMap = React.useMemo(() => buildFamilyMap(families), [families])

  const handleUpdateFamily = React.useCallback(
    async (specieId, newFamilyId) => {
      const currentRow = data.find(row => row.id === specieId)
      if (!currentRow) return

      setFamilyUpdateBySpecieId(prev => ({
        ...prev,
        [specieId]: { status: 'loading', message: null },
      }))

      const TOKEN = `Bearer ${window.localStorage.getItem('ArbolEc_token')}`
      const payload = {
        nombre_cientifico: currentRow.nombre_cientifico,
        clasificador: currentRow.clasificador,
        idFamilia: newFamilyId,
        habilitado: currentRow.habilitado,
      }

      try {
        const result = await updateSpecieTableById(payload, TOKEN, specieId)
        if (!result?.update) {
          throw new Error(result?.message || 'No se pudo actualizar la familia.')
        }

        setData(prev =>
          prev.map(row => {
            if (row.id !== specieId) return row
            const familyData = familyMap[`${newFamilyId}`]
            const familiaActiva = Boolean(familyData?.habilitado)
            return {
              ...row,
              idFamilia: newFamilyId,
              familiaNombre: familyData?.nombre || INACTIVE_GROUP_LABEL,
              familiaActiva,
              familyGroupKey: familiaActiva ? `${newFamilyId}` : INACTIVE_GROUP_KEY,
            }
          })
        )

        setFamilyUpdateBySpecieId(prev => ({
          ...prev,
          [specieId]: { status: 'success', message: null },
        }))
      } catch (error) {
        setFamilyUpdateBySpecieId(prev => ({
          ...prev,
          [specieId]: {
            status: 'error',
            message: error?.message || 'No se pudo actualizar la familia.',
          },
        }))
      }
    },
    [data, familyMap]
  )

  const onToggleRow = React.useCallback(rowIdToToggle => {
    setExpandedRowIds(prev =>
      prev.includes(rowIdToToggle)
        ? prev.filter(id => id !== rowIdToToggle)
        : [...prev, rowIdToToggle]
    )
  }, [])

  const columns = React.useMemo(
    () =>
      getColumns(
        rowId,
        setRowId,
        expandedRowIds,
        onToggleRow,
        enabledFamilyOptions,
        handleUpdateFamily,
        familyUpdateBySpecieId,
        handleRequestDelete
      ),
    [
      rowId,
      expandedRowIds,
      onToggleRow,
      enabledFamilyOptions,
      handleUpdateFamily,
      familyUpdateBySpecieId,
      handleRequestDelete,
    ]
  )

  const familyFilterOptions = React.useMemo(() => buildFamilyFilterOptions(families), [families])

  const selectedFamilyOptions = React.useMemo(
    () => familyFilterOptions.filter(option => selectedFamilyKeys.includes(option.key)),
    [familyFilterOptions, selectedFamilyKeys]
  )

  const filteredBaseRows = React.useMemo(
    () => filterRows(data, scientificSearch, classifierSearch, selectedFamilyKeys),
    [data, scientificSearch, classifierSearch, selectedFamilyKeys]
  )

  const rows = React.useMemo(() => {
    const expanded = new Set(expandedRowIds)
    const mappedRows = []

    filteredBaseRows.forEach(specie => {
      mappedRows.push(specie)
      if (expanded.has(specie.id)) {
        mappedRows.push({
          ...specie,
          id: `detail-${specie.id}`,
          __isDetail: true,
          __parentId: specie.id,
        })
      }
    })

    return mappedRows
  }, [filteredBaseRows, expandedRowIds])

  React.useEffect(() => {
    const visibleIds = new Set(filteredBaseRows.map(row => row.id))
    setExpandedRowIds(prev => prev.filter(id => visibleIds.has(id)))
  }, [filteredBaseRows])

  React.useEffect(() => {
    const visibleIds = new Set(data.map(row => row.id))
    setFamilyUpdateBySpecieId(prev => {
      const next = {}
      Object.entries(prev).forEach(([specieId, state]) => {
        if (visibleIds.has(Number(specieId))) next[specieId] = state
      })
      return next
    })
  }, [data])

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const [speciesRows, familyRows] = await Promise.all([getSpecieTableAll(), getFamilyTableAll()])
      const normalizedFamilies = normalizeFamilies(familyRows)
      const familyMap = buildFamilyMap(normalizedFamilies)
      const enrichedRows = enrichSpeciesRows(speciesRows, familyMap)

      setFamilies(normalizedFamilies)
      setData(enrichedRows)
    } catch (err) {
      console.log('Error: ', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleFormCreation = () => {
    modalRef.current.openModal()
  }

  React.useEffect(() => {
    loadData()
  }, [loadData])

  React.useEffect(() => {
    if (!Boolean(isStored)) return
    loadData()
      .then(() => {
        alertRef?.current?.openAlert(true)
      })
      .catch(err => console.log('Error: ', err))
  }, [isStored, loadData])

  return (
    <Box sx={styleContainer}>
      <Box sx={styleHeaderTab}>
        <Box>
          <Typography>Administrar Especies</Typography>
        </Box>
        <Box>
          <Botton
            text="Nuevo"
            onClick={handleFormCreation}
            startIcon={<Create />}
            styleButton={styleDefault}
            size="small"
          />
        </Box>
      </Box>

      <Paper elevation={1} sx={{ mx: 4, mb: 1.5, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="Buscar por nombre científico"
            placeholder="Ej: Quercus robur"
            value={scientificSearch}
            onChange={event => setScientificSearch(event.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label="Buscar por clasificador"
            placeholder="Ej: Linnaeus"
            value={classifierSearch}
            onChange={event => setClassifierSearch(event.target.value)}
          />

          <Autocomplete
            multiple
            fullWidth
            size="small"
            options={familyFilterOptions}
            value={selectedFamilyOptions}
            onChange={(_, newValue) => setSelectedFamilyKeys(newValue.map(item => item.key))}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.key} label={option.label} size="small" />
              ))
            }
            renderInput={params => <TextField {...params} label="Filtrar por familias" />}
          />
        </Stack>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 2, display: 'flex' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          getRowId={row => row.id}
          rowsPerPageOptions={[20, 50, 100]}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          isCellEditable={params => !params.row?.__isDetail && Boolean(params.colDef.editable)}
          getRowHeight={params => (params.model?.__isDetail ? 'auto' : null)}
          getEstimatedRowHeight={() => 120}
          getRowClassName={params => (params.row?.__isDetail ? 'specie-detail-row' : '')}
          getRowSpacing={params => ({
            top: params.model?.__isDetail ? 0 : params.isFirstVisible ? 0 : 5,
            bottom: params.model?.__isDetail ? 8 : 0,
          })}
          sx={{
            flex: 1,
            [`& .${gridClasses.row}`]: {
              bgcolor: grey[100],
            },
            '& .specie-detail-row': {
              bgcolor: '#F9FAFC !important',
            },
            '& .specie-detail-cell': {
              py: '0 !important',
              alignItems: 'flex-start',
            },
            '& .specie-expand-cell': {
              pl: 0.5,
            },
          }}
          loading={loading}
          onCellEditCommit={params => {
            if (typeof params.id === 'string' && params.id.startsWith('detail-')) {
              return
            }
            setRowId(params.id)
          }}
        />
      </Box>

      <CreationModal ref={modalRef} />
      <DeleteSpecieModal ref={deleteModalRef} />
      <AlertCustom
        ref={alertRef}
        severity={isStored?.stored !== true ? 'warning' : 'success'}>
        {isStored?.stored !== true ? isStored?.message : 'Guardado exitosamente.'}
      </AlertCustom>
    </Box>
  )
}

export default SpecieTable
